import { AbortMultipartUploadCommand, CompletedPart, CompleteMultipartUploadCommand, CopyObjectCommand, CreateMultipartUploadCommand, CreateMultipartUploadCommandOutput, DeleteObjectCommand, GetObjectCommand, HeadObjectCommand, HeadObjectCommandOutput, ListObjectsV2Command, ListObjectsV2CommandOutput, PutObjectCommand, UploadPartCommand } from "@aws-sdk/client-s3";
import { downloadLink, generateDocumentViewer, isDocumentFile, isVideo, isImage } from "../../app/utils";
import APIError from "../api/APIError";
import { handleFetchError } from "../api/request";
import configuration from "../configuration/Configuration"
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { InvokeCommand, LogType } from "@aws-sdk/client-lambda";
import { ProjectSession } from "./ProjectsSlice";
import { ClientFactory } from "../client/factory";
import { Cache } from "../api/cache";

const HIDE_USERS = ['amministratore@meraki.it', 'admin@meraki.it'];

export type RepoId = string;

type ProjectId = RepoId;

type FolderId = RepoId;

type PermissionType = "r" | "w"

export type Permissions = {
  [projectId: string]: {
    folders: {
      [folderName: string]: PermissionType[];
    }
    metadata: {
      name: string;
    }
  }
}

export type Project = {
  id: ProjectId;
  name: string;
  folders: string[];
}

const cache = new Cache();

export const loadMine = async (token: string): Promise<Project[]> => {
  return cache.getOrProduce(
    "projectList",
    async () => {
      const clientFactory = ClientFactory.getInstance();
      const lambdaClient = clientFactory.getLambdaClient(token);
      
      const command = new InvokeCommand({
        FunctionName: configuration.authLambdaFunctionName,
        Payload: JSON.stringify({ token: `Bearer ${token}` }),
        LogType: LogType.None,
      })

      const { Payload } = await lambdaClient.send(command)

      const textDecoder = new TextDecoder("utf-8")
      const payloadString = textDecoder.decode(Payload || new Uint8Array())
      const payload = JSON.parse(payloadString)
      if (payload.statusCode !== 200) {
        throw new Error("Failed to retrieve credentials")
      }
      const body: { permissions: Permissions } = payload.body ? JSON.parse(payload.body) : {}
      const list = Object.entries(body.permissions).map(([projectId, projectData]) => ({
        id: projectId as ProjectId,
        name: projectData.metadata.name,
        folders: Object.keys(projectData.folders).map(folder => `/${folder}`),
      })).filter(project => project.folders.length > 0);
      return list;
    },
  )
}

export const getProjectToken =
  async (
    token: string,
    projectId: string
  ): Promise<ProjectSession> => {
    return cache.getOrProduce(
      `projectToken-${projectId}`,
      async () => {
        const clientFactory = ClientFactory.getInstance();
        const lambdaClient = clientFactory.getLambdaClient(token);
        
        const command = new InvokeCommand({
          FunctionName: configuration.authLambdaFunctionName,
          Payload: JSON.stringify({ token: `Bearer ${token}`, projectId }),
          LogType: LogType.None,
        })

        const { Payload } = await lambdaClient.send(command)

        const textDecoder = new TextDecoder("utf-8")
        const payloadString = textDecoder.decode(Payload || new Uint8Array())
        const payload = JSON.parse(payloadString)
        if (payload.statusCode !== 200) {
          throw new Error("Failed to retrieve credentials")
        }
        const body = payload.body ? JSON.parse(payload.body) : {}
        if (!body.credentials.SessionToken) {
          throw new Error("Failed to retrieve credentials")
        }
        return body.credentials;
      },
      10000
    )
  }



export type ServerStatus = "up" | "down" | "unknown";

export const getStatus = async (): Promise<ServerStatus> => {
  await fetch(
    `s3.${configuration.region}.amazonaws.com/${configuration.bucketName}`,
    { method: 'HEAD' }
  )
  return "up";
}

export type ProjectMember = {
  user_info: { nickname: string; name: string },
  share_type: string;
}

type Participant = {
  userSub: string;
  nickname: string;
  type: "employee" | "admin" | "user";
};

type ProjectParticipants = {
  projectId: string;
  participants: Participant[];
};

export const loadProjectMembers = async (
  projectId: string, folderIds: FolderId[], sessionToken: string,
): Promise<ProjectMember[]> => {
  return cache.getOrProduce(
    `projectMembers-${projectId}`,
    async () => {
      const clientFactory = ClientFactory.getInstance();
      const lambdaClient = clientFactory.getLambdaClient(sessionToken);
      const command = new InvokeCommand({
        FunctionName: "MerakiListProjectUsers",
        Payload: JSON.stringify({ token: `Bearer ${sessionToken}`, projectId }),
        LogType: LogType.None,
      })

      const { Payload } = await lambdaClient.send(command)

      const textDecoder = new TextDecoder("utf-8")
      const payloadString = textDecoder.decode(Payload || new Uint8Array())
      const payload = JSON.parse(payloadString)
      if (payload.statusCode !== 200) {
        throw new Error("Failed to retrieve credentials")
      }
      const body: ProjectParticipants = payload.body ? JSON.parse(payload.body) : {}
      return body.participants.map(participant => ({
        user_info: {
          nickname: participant.nickname,
          name: participant.userSub,
        },
        share_type: participant.type,
      })).filter(member => HIDE_USERS.indexOf(member.user_info.name) === -1)
    }
  )
}

export type ProjectFile = {
  id: string;
  type: "file";
  name: string;
  size: number;
  mtime: number;
}
export type ProjectDir = {
  id: string;
  type: "dir";
  name: string;
  mtime: number;
}
export type ProjectEntity = ProjectFile | ProjectDir;

export const loadRepo = async (
  repoId: RepoId, folder: string, sessionToken: ProjectSession
): Promise<ProjectEntity[]> => {
  const clientFactory = ClientFactory.getInstance();
  const client = clientFactory.getS3Client(sessionToken);

  const command = new ListObjectsV2Command({
    Bucket: configuration.bucketName,
    Prefix: repoId + (folder.endsWith("/") ? folder : folder + "/"),
    Delimiter: "/",
  });

  let responseContent: ProjectEntity[] = [];
  try {
    const response: ListObjectsV2CommandOutput = await client.send(command);

    const files = response.Contents?.filter(obj => obj.Key && !obj.Key.endsWith("/")) || [];

    responseContent = files.map(file => ({
      id: file.Key!,
      type: "file",
      name: file.Key!.split("/").pop() || "",
      size: file.Size || 0,
      mtime: file.LastModified ? new Date(file.LastModified).getTime() : 0,
    }));
  } catch (err) {
    console.error("Error loading S3 folder:", err)
  }
  const withoutThumbnails = responseContent.filter(
    entity => !entity.name.endsWith('.tmb')
  ).sort((a, b) => b.mtime - a.mtime);
  return withoutThumbnails;
}

type DownloadLink = string;

export const generateFileDownloadLink = async (
  repoId: RepoId, folder: string, filename: string, sessionToken: ProjectSession,
  seconds: number = 3600
): Promise<DownloadLink> => {
  const key = `${repoId}${folder}/${filename}`.replace(/\/+/g, "/");
  return cache.getOrProduce(
    `genFileDL-${key}`,
    async () => {
      if (seconds < 60*5) seconds = 60*5;
      const clientFactory = ClientFactory.getInstance();
      const s3 = clientFactory.getS3Client(sessionToken);

      // check if file exists
      await s3.send(new HeadObjectCommand({
        Bucket: configuration.bucketName,
        Key: key,
      }));

      const command = new GetObjectCommand({
        Bucket: configuration.bucketName,
        Key: key,
      });

      try {
        const signedUrl = await getSignedUrl(s3, command, {
          expiresIn: seconds,
        });
        return signedUrl;
      } catch (err) {
        console.error("Error generating download link:", err);
        throw new Error("Failed to generate download link");
      }
    },
    60*5 - 1
  )
}

type PreviewAPIResponse = {
  url: string
  thumbnail?: string
}

export const getFilePreview = async (
  repoId: RepoId, folder: string, file: ProjectEntity, sessionToken: string, forceCreation: boolean = true
): Promise<PreviewAPIResponse> => {
  const host = configuration.adminApiHost;
  let url = `${host}/video/${repoId}/${encodeURIComponent(folder)}/${encodeURIComponent(file.name)}?createIfNotExists=${forceCreation}`
  if (file.mtime)
    url += `&mtime=${file.mtime}`
  const response = await fetch(
    url,
    {
      method: 'GET',
      cache: 'no-cache',
      headers: {
        'Authorization': `Token ${sessionToken}`
      },
      redirect: 'follow',
    }
  ).catch(handleFetchError);
  if(!response.ok){
    if(response.status === 404)
      throw new Error("File non trovato");
    else if(response.status === 400)
      throw new Error("La sezione indicata non appartiene al progetto");
    else
      throw await APIError.build(response);
  }
  const apiResponse: PreviewAPIResponse = await response.json();
  return apiResponse;
}

export const getOpenLink = async (
  repoId: RepoId, folder: string, filename: string, sessionToken: ProjectSession
): Promise<string> => {
  let viewUrl;
  const downloadUrl: string = await generateFileDownloadLink(
    repoId, folder, filename, sessionToken
  );
  if(isDocumentFile(filename)){
    try{
      viewUrl = generateDocumentViewer(downloadUrl);
    }catch{}
  }
  if(!viewUrl){
    return downloadUrl;
  }
  return viewUrl;
}

export const deleteFile = async (
  repoId: RepoId, folder: string, filename: string, sessionToken: ProjectSession
): Promise<void> => {
  const clientFactory = ClientFactory.getInstance();
  const s3 = clientFactory.getS3Client(sessionToken);

  const key = `${repoId}${folder}/${filename}`.replace(/\/+/g, "/");

  const command = new DeleteObjectCommand({
    Bucket: configuration.bucketName,
    Key: key,
  });

  await s3.send(command)

  const toDeleteList = getThumbnailNames(filename);
  for (const toDelete of toDeleteList) {
    try{
      await deleteFile(
        repoId, folder,
        toDelete,
        sessionToken
      );
    }catch{}
  }
}

export const renameFile = async (
  repoId: RepoId, folder: string, filename: string, newFileName: string,
  sessionToken: ProjectSession
): Promise<void> => {
  const clientFactory = ClientFactory.getInstance();
  const s3 = clientFactory.getS3Client(sessionToken);
  
  let newKey;
  let isNew = false;
  let nameIndex = 0;
  let newName = newFileName;
  const onlyName = newFileName.split('.').slice(0, -1).join('.');
  const extension = newFileName.split('.').pop();

  while(!isNew) {
    try {
      newKey = `${repoId}${folder}/${newName}`.replace(/\/+/g, "/");
      await s3.send(new HeadObjectCommand({
        Bucket: configuration.bucketName,
        Key: newKey
      }));
      nameIndex++;
      newName = `${onlyName} (${nameIndex}).${extension}`;
    } catch {
      isNew = true;
    }
  }

  const sourceKey = `${repoId}${folder}/${filename}`.replace(/\/+/g, "/");

  const copyCommand = new CopyObjectCommand({
    Bucket: configuration.bucketName,
    Key: newKey,
    CopySource: `${configuration.bucketName}/${sourceKey}`
  });

  await s3.send(copyCommand)

  const deleteCommand = new DeleteObjectCommand({
    Bucket: configuration.bucketName,
    Key: sourceKey,
  });

  await s3.send(deleteCommand)
}

export type ParsingType = "json" | "blob";

export type DownloadOptions = {
  getInApp?: boolean;
  parseResponse?: ParsingType;
}

function getThumbnailNames(filename: string) {
  const names: string[] = [];
  if(isVideo(filename) || isImage(filename)){
    const filenameSlices = filename.split('.');
    names.push(
      `${filenameSlices.slice(0, -1).join('.')}_${filenameSlices[filenameSlices.length - 1]}.256.tmb`
    );
    if (isImage(filename)) {
      names.push(
        `${filenameSlices.slice(0, -1).join('.')}_${filenameSlices[filenameSlices.length - 1]}.1280.tmb`
      );
    }
    names.push(
      `${filenameSlices.slice(0, -1).join('.')}_${filenameSlices[filenameSlices.length - 1]}.tmb`,
      `${filenameSlices.slice(0, -1).join('.')}.tmb`
    );
  }
  return names;
}

type SignedS3Url = string

export async function getFile(url: SignedS3Url, parsing?: ParsingType){
  const response = await fetch(
    url,
    {
      method: 'GET',
      cache: 'no-cache',
      redirect: 'follow',
    }
  ).catch(handleFetchError);
  if(!response.ok){
    throw await APIError.build(response);
  }
  const objectResponse: any =
    parsing === 'json' ? await response.json() : await response.blob();
  return objectResponse;
}

export async function getFileDetails(
  repoId: string, folderId: FolderId, fileId: string, sessionToken: ProjectSession
): Promise<ProjectEntity>{
  const clientFactory = ClientFactory.getInstance();
  const client = clientFactory.getS3Client(sessionToken);
  const key = repoId + (folderId.endsWith("/") ? folderId : folderId + "/") + fileId;
  const command = new HeadObjectCommand({
    Bucket: configuration.bucketName,
    Key: key,
  });

  try {
    const response: HeadObjectCommandOutput = await client.send(command);

    return {
      id: key,
      type: "file",
      name: fileId,
      size: response.ContentLength || 0,
      mtime: response.LastModified ? new Date(response.LastModified).getTime() : 0,
    };
  } catch (err) {
    throw new Error("File non trovato");
  }
}

export const downloadFile = async (
  repoId: RepoId, folder: string, filename: string, sessionToken: ProjectSession,
  options?: DownloadOptions
): Promise<any> => {
  const downloadUrl: string = await generateFileDownloadLink(
    repoId, folder, filename, sessionToken
  );
  if(!options?.getInApp)
    return await downloadLink(downloadUrl, filename)
        .then(res => null)
        .catch(err => {
            throw new Error(`Impossibile scaricare il file ${filename}`);
        });
  else
    return await getFile(downloadUrl, options?.parseResponse);
}

export const generateFileUploadParent = async (
  repoId: string, folder: FolderId, filename: string, contentType: string, sessionToken: ProjectSession
): Promise<CreateMultipartUploadCommandOutput> => {
  const clientFactory = ClientFactory.getInstance()
  const s3 = clientFactory.getS3Client(sessionToken)
  const s3UploadParams = {
    Bucket: configuration.bucketName,
    Key: `${repoId}${folder}/${filename}`,
    ContentType: contentType,
  }
  const multipartUpload = await s3.send(
    new CreateMultipartUploadCommand(s3UploadParams),
  )
  return multipartUpload;
}

export type UploadOptions = {
  toReplace?: ProjectEntity;
}

export type UploadedFile = {
  name: string;
  id: string;
  size: number;
}

export const uploadFile = async (
  repoId: RepoId, folder: string, file: File, sessionToken: ProjectSession,
  options?: UploadOptions
): Promise<UploadedFile[]> => {
  const clientFactory = ClientFactory.getInstance()
  const s3 = clientFactory.getS3Client(
    sessionToken,
    { requestChecksumCalculation: "WHEN_REQUIRED" }
  )
  const filename = options?.toReplace?.name || file.name;
  const key = `${repoId}${folder}/${filename}`
  const putObjectCommand = new PutObjectCommand({
    Bucket: configuration.bucketName,
    Key: key,
    Body: file,
    ContentType: file.type ? file.type : "application/octet-stream",
  });
  await s3.send(putObjectCommand);
  return [{
    name: filename,
    id: key,
    size: file.size,
  }];
}

export type ChunkUploadOptions = UploadOptions & {
  parentUpload?: CreateMultipartUploadCommandOutput;
  etags?: { value: string; partNumber: number }[];
  controller?: AbortController
}

export type Progress = {
  loaded: number;
  total: number;
}

export const uploadChunks = async (
  repoId: RepoId, folder: string, filename: string, file: File,
  maxSize: number, progressHandler: (progress: Progress) => void,
  sessionToken: ProjectSession, controller?: AbortController
): Promise<boolean> => {
  /* ── constants & validations ──────────────────────────────── */
  const MIN_PART = 5 * 1024 * 1024;              // S3 hard-minimum 5 MiB
  const MAX_PART = 5 * 1024 * 1024 * 1024;       // S3 hard-maximum 5 GiB
  const Bucket   = configuration.bucketName;
  const Key      = `${repoId}${folder}/${filename}`;

  let partSize = Math.max(maxSize, MIN_PART);    // obey S3 minimum
  if (partSize > MAX_PART) {
    throw new Error(`partSize (${partSize}) exceeds 5 GiB S3 limit`);
  }

  /* If the caller gave a *very* small part size that would create
     >10 000 parts, we bump it up so we meet S3’s 10 000-part limit. */
  const calcPartCount = (size: number, psize: number) =>
    Math.ceil(size / psize);

  if (calcPartCount(file.size, partSize) > 10_000) {
    partSize = Math.ceil(file.size / 10_000);
    partSize = Math.max(partSize, MIN_PART);
    if (partSize > MAX_PART) {
      throw new Error(
        `File is too large (${file.size} bytes) to upload with the given maxSize; ` +
        `it would need parts >5 GiB which S3 forbids.`
      );
    }
  }

  /* ── S3 client ─────────────────────────────────────────────── */
  const s3 = ClientFactory
    .getInstance()
    .getS3Client(sessionToken, { requestChecksumCalculation: "WHEN_REQUIRED" });

  /* ── initiate multipart upload ─────────────────────────────── */
  const { UploadId } = await s3.send(
    new CreateMultipartUploadCommand({ Bucket, Key })
  );
  if (!UploadId) throw new Error("Failed to start multipart upload");

  /* ── upload parts ──────────────────────────────────────────── */
  const totalParts = calcPartCount(file.size, partSize);
  const completedParts: CompletedPart[] = [];
  let bytesUploaded = 0;

  try {
    for (let partNumber = 1; partNumber <= totalParts; partNumber++) {
      if (controller?.signal.aborted) throw new Error("Upload aborted");

      const start = (partNumber - 1) * partSize;
      const end   = Math.min(start + partSize, file.size);
      const blob  = file.slice(start, end);

      const { ETag } = await s3.send(
        new UploadPartCommand({
          Bucket,
          Key,
          UploadId,
          PartNumber: partNumber,
          Body: blob,
        }),
        { abortSignal: controller?.signal }
      );

      completedParts.push({ ETag, PartNumber: partNumber });

      bytesUploaded += end - start;
      progressHandler({ loaded: bytesUploaded, total: file.size });
    }

    /* ── complete ────────────────────────────────────────────── */
    await s3.send(
      new CompleteMultipartUploadCommand({
        Bucket,
        Key,
        UploadId,
        MultipartUpload: { Parts: completedParts },
      })
    );

    return true;
  } catch (err) {
    /* ── clean up on failure / abort ─────────────────────────── */
    await s3.send(
      new AbortMultipartUploadCommand({ Bucket, Key, UploadId })
    );
    throw err;
  }
};

export const getFileCustomThumbnail = async (
  repoId: RepoId, folder: string, file: ProjectEntity, sessionToken: ProjectSession, size: number
): Promise<string | undefined> => {
  const filenameSlices = file.name.split('.');
  const thumbnailName1 = `${filenameSlices.slice(0, -1).join('.')}.tmb`;
  const fileExtension = filenameSlices[filenameSlices.length - 1];
  const thumbnailName2 =
    `${filenameSlices.slice(0, -1).join('.')}_${fileExtension}.tmb`;
  const thumbnailName3 = `${filenameSlices.slice(0, -1).join('.')}_${fileExtension}.${size}.tmb`;
  let thumbnail = undefined;
  try{
    try{
      thumbnail = await generateFileDownloadLink(
        repoId, folder, thumbnailName3, sessionToken
      );
    }catch{
      if(size === 256 && file.mtime < 1676505600){
        try{
          thumbnail = await generateFileDownloadLink(
            repoId, folder, thumbnailName2, sessionToken
          );
        } catch {
          thumbnail = await generateFileDownloadLink(
            repoId, folder, thumbnailName1, sessionToken
          );
        }
      }
    }
  }catch{
    thumbnail = undefined;
  }
  return thumbnail;
}

export type ThumbnailOptions = {
  size?: number;
}

export const getFileThumbnail = async (
  repoId: RepoId, folder: string, file: ProjectEntity, sessionToken: ProjectSession,
  thumbnailOptions?: ThumbnailOptions
): Promise<{ url: string | null, cachable: boolean }> => {
  try{
    const customThumbnail = await getFileCustomThumbnail(
      repoId, folder, file, sessionToken, thumbnailOptions?.size || 256
    );
    if(customThumbnail) return { url: customThumbnail, cachable: true };
  }catch{}
  return { url: null, cachable: false }
}
